import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users, Plus, Trash2, ToggleLeft, ToggleRight, Send, ChevronDown, ChevronRight } from "lucide-react";
import { Card, Button, Pill } from "./common/Primitives";
import api, { formatApiError } from "../lib/api";

const SEGMENTS = [
  { key: "include_startups", label: "Startups", hint: "All active startups (respects opt-outs)" },
  { key: "include_investors", label: "Investors", hint: "All active investors (respects opt-outs)" },
  { key: "include_admins", label: "Admins", hint: "Super admins, admins, managers, agents" },
  { key: "include_extras", label: "External list", hint: "Custom recipients you add below" },
];

export default function DistributionSettings() {
  const [expanded, setExpanded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [count, setCount] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newTag, setNewTag] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/market-report/settings"),
        api.get("/admin/market-report/recipients"),
      ]);
      setSettings(s.data.settings);
      setCount(s.data.recipient_count);
      setRecipients(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patchSetting = async (key, value) => {
    setSavingKey(key);
    try {
      const { data } = await api.put("/admin/market-report/settings", { [key]: value });
      setSettings(data.settings);
      setCount(data.recipient_count);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  const addRecipient = async () => {
    if (!newEmail.trim()) { toast.error("Email is required"); return; }
    setAdding(true);
    try {
      await api.post("/admin/market-report/recipients", {
        email: newEmail.trim(),
        name: newName.trim() || null,
        tag: newTag.trim() || null,
        active: true,
      });
      toast.success("Recipient added");
      setNewEmail(""); setNewName(""); setNewTag("");
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/admin/market-report/recipients/${r.recipient_id}`, { active: !r.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRecipient = async (r) => {
    if (!confirm(`Remove ${r.email} from the distribution list?`)) return;
    try {
      await api.delete(`/admin/market-report/recipients/${r.recipient_id}`);
      toast.success("Recipient removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card testId="admin-mr-distribution" className="mb-6 p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-navy/[0.03] transition-colors"
        data-testid="admin-mr-distribution-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Users size={14} />
          <span className="font-heading text-sm">Distribution list</span>
          <Pill tone="muted"><Send size={10} className="inline mr-1" /> {count} recipients</Pill>
        </div>
        <span className="text-xs text-ink-muted">{expanded ? "Collapse" : "Configure"}</span>
      </button>

      {expanded && (
        <div className="p-4 border-t border-navy/[0.06] space-y-6">
          {loading && !settings && (
            <div className="text-ink-muted text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
          )}

          {settings && (
            <>
              {/* Segment toggles */}
              <div>
                <div className="overline mb-2">Audience segments</div>
                <div className="grid gap-2 md:grid-cols-2">
                  {SEGMENTS.map((s) => {
                    const on = !!settings[s.key];
                    const busy = savingKey === s.key;
                    return (
                      <button
                        key={s.key}
                        onClick={() => patchSetting(s.key, !on)}
                        disabled={busy}
                        className={`flex items-start gap-3 text-left p-3 rounded border transition-colors ${
                          on ? "border-emerald-500/40 bg-emerald-500/5" : "border-navy/10 bg-white hover:bg-navy/[0.02]"
                        }`}
                        data-testid={`admin-mr-toggle-${s.key}`}
                      >
                        {busy
                          ? <Loader2 className="animate-spin mt-0.5" size={18} />
                          : on
                            ? <ToggleRight size={18} className="text-emerald-600 mt-0.5" />
                            : <ToggleLeft size={18} className="text-ink-faint mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-heading text-ink">{s.label}</div>
                          <div className="text-xs text-ink-soft">{s.hint}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {settings.updated_at && (
                  <div className="mt-2 text-[11px] text-ink-faint font-mono">
                    Last changed {settings.updated_at.slice(0, 16).replace("T", " ")} by {settings.updated_by}
                  </div>
                )}
              </div>

              {/* External recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">External recipients ({recipients.length})</div>
                  <Button variant="dark" onClick={() => setAddOpen((v) => !v)} data-testid="admin-mr-add-toggle">
                    <Plus size={12} className="inline mr-1" /> Add recipient
                  </Button>
                </div>

                {addOpen && (
                  <div className="mb-3 p-3 rounded border border-navy/10 bg-navy/[0.02] grid gap-2 md:grid-cols-4" data-testid="admin-mr-add-form">
                    <input
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="email@partner.com"
                      className="col-span-2 text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
                      data-testid="admin-mr-add-email"
                    />
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Display name (optional)"
                      className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
                      data-testid="admin-mr-add-name"
                    />
                    <input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Tag (optional)"
                      className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
                      data-testid="admin-mr-add-tag"
                    />
                    <div className="col-span-full flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                      <Button variant="primary" onClick={addRecipient} disabled={adding} data-testid="admin-mr-add-save">
                        {adding ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <Plus size={12} className="inline mr-1" />}
                        Add
                      </Button>
                    </div>
                  </div>
                )}

                {recipients.length === 0 ? (
                  <div className="text-sm text-ink-muted py-3">No external recipients yet.</div>
                ) : (
                  <div className="overflow-x-auto rounded border border-navy/10">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-ink-muted bg-navy/[0.02]">
                        <tr>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2">Tag</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((r) => (
                          <tr key={r.recipient_id} className="border-t border-navy/[0.06]" data-testid={`admin-mr-recipient-${r.recipient_id}`}>
                            <td className="px-3 py-2 font-mono text-xs text-ink">{r.email}</td>
                            <td className="px-3 py-2 text-ink-soft">{r.name || "—"}</td>
                            <td className="px-3 py-2 text-ink-soft">{r.tag ? <Pill tone="muted">{r.tag}</Pill> : "—"}</td>
                            <td className="px-3 py-2">
                              <Pill tone={r.active ? "success" : "muted"}>{r.active ? "Active" : "Paused"}</Pill>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                              <Button variant="ghost" onClick={() => toggleActive(r)} data-testid={`admin-mr-toggle-${r.recipient_id}`}>
                                {r.active ? <ToggleRight size={14} className="inline" /> : <ToggleLeft size={14} className="inline" />}
                              </Button>
                              <Button variant="ghost" onClick={() => deleteRecipient(r)} data-testid={`admin-mr-delete-${r.recipient_id}`}>
                                <Trash2 size={14} className="inline" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
